import { Employee } from "types/employee";
import { getBasePathWithoutTrailingSlash } from "utils/basePath";
import { getBrandLogoUrl } from "utils/basePath";
import { DEFAULT_DEMO_BRANDING } from "theme";

export const PRODUCT_BRAND_NAME = "YourQcard";

/** Demo company shown in marketing previews (dashboard + digital card). */
export const DEMO_COMPANY_NAME = "YourQcard";

export const DASHBOARD_PREVIEW_STATS = [
  { label: "Total employees", value: "48" },
  { label: "QR scans (30d)", value: "1.2k" },
  { label: "Active licenses", value: "12" },
] as const;

export const DASHBOARD_PREVIEW_ROWS = [
  {
    name: "Sarah Mitchell",
    role: "Senior Solutions Architect",
    status: "Active" as const,
    scans: "142",
  },
  { name: "James Okonkwo", role: "Security Lead", status: "Active" as const, scans: "89" },
  { name: "Lina Hassan", role: "HR Manager", status: "Active" as const, scans: "56" },
  {
    name: "Marco Rossi",
    role: "Consultant",
    status: "Pending" as const,
    scans: "12",
  },
] as const;

export const DASHBOARD_PREVIEW_NAV = [
  "Dashboard",
  "Employees",
  "Users",
  "Licenses",
  "Settings",
] as const;

/** Partner logos shown on the homepage trust strip */
export const TRUSTED_PARTNERS = [
  {
    id: "ids",
    name: "IDS",
    sector: "Enterprise technology",
    description: "Digital identity & workforce solutions",
  },
  {
    id: "softhub",
    name: "SoftHub",
    sector: "Software & integration",
    description: "Custom platforms and business automation",
  },
  {
    id: "idsFintech",
    name: "IDS Fintech Tech",
    sector: "Fintech & payments",
    description: "Financial technology and secure transactions",
  },
] as const;

export const TRUSTED_INDUSTRIES = [
  "Finance",
  "Technology",
  "Professional services",
  "Healthcare",
  "Government",
] as const;

/** Three pillars for homepage   deep detail lives on /features */
export const HOME_BENEFITS = [
  {
    title: "Create digital cards",
    description:
      "Create branded digital profiles for individuals, employees, and teams with QR-powered sharing.",
  },
  {
    title: "Manage teams & identities",
    description:
      "Manage users, licenses, digital profiles, and company branding from one centralized dashboard.",
  },
  {
    title: "Share & verify instantly",
    description:
      "Share digital profiles through QR codes, profile links, or PDF exports and verify identities in real time.",
  },
] as const;

/** Full feature list aligned with the YourQcard application */
export const APP_FEATURES = [
  { title: "Employee profiles", description: "Digital cards with photo, role, contact details, and company branding.", category: "Core" },
  { title: "QR code generation", description: "Unique QR per employee linking to a public verification page.", category: "Core" },
  { title: "Company dashboard", description: "Metrics, employee list, license usage, and QR scan activity.", category: "Core" },
  { title: "License packages", description: "Purchase and upgrade seat-based licenses for your organization.", category: "Admin" },
  { title: "User & role management", description: "Platform admins, company owners, and managers with permission gates.", category: "Admin" },
  { title: "Company profiles", description: "Brand colors, logos, and settings applied across all employee cards.", category: "Admin" },
  { title: "Contact capture", description: "Inbound contact requests managed from the backoffice.", category: "Admin" },
  { title: "vCard export", description: "Visitors save employee contact details to their phone in one tap.", category: "Sharing" },
  { title: "PDF card download", description: "Export printable QR cards for badges or email signatures.", category: "Sharing" },
  { title: "License expiry handling", description: "Expired or missing licenses are surfaced clearly on public cards.", category: "Security" },
  { title: "reCAPTCHA protection", description: "Forms and public endpoints protected against abuse.", category: "Security" },
  { title: "Multi-tenant companies", description: "Each organization operates in an isolated company workspace.", category: "Security" },
] as const;

export const MARKETING_FEATURES = [
  {
    title: "Branded digital ID cards",
    description:
      "Issue professional employee cards with your logo, colors, and role details always up to date, never reprinted.",
    icon: "card",
    imageKey: "design" as const,
  },
  {
    title: "Instant QR verification",
    description:
      "Anyone can scan a QR code to confirm identity, role, and license validity in real time at gates, events, or client sites.",
    icon: "qr",
    imageKey: "security" as const,
  },
  {
    title: "Centralized admin dashboard",
    description:
      "Manage companies, licenses, employees, and packages from one backoffice built for HR, security, and IT teams.",
    icon: "dashboard",
    imageKey: "management" as const,
  },
  {
    title: "Roles & permissions",
    description:
      "Fine-grained access for platform admins, company owners, and managers with audit-friendly activity tracking.",
    icon: "shield",
    imageKey: "security" as const,
  },
  {
    title: "Bulk employee management",
    description:
      "Add teams manually or import at scale. Generate QR codes, share profiles, and export PDF cards in clicks.",
    icon: "users",
    imageKey: "management" as const,
  },
  {
    title: "License-based scaling",
    description:
      "Purchase packages that match your headcount. Upgrade anytime as your organization grows.",
    icon: "license",
    imageKey: "design" as const,
  },
] as const;

export const HOW_IT_WORKS_STEPS = [
  {
    step: 1,
    title: "Choose your plan",
    description: "Select the package that fits your personal or business needs.",
  },
  {
    step: 2,
    title: "Create your profile or company",
    description: "Set up your personal profile or company branding in minutes.",
  },
  {
    step: 3,
    title: "Add team members",
    description: "Invite employees and manage digital identities from one dashboard",
  },
  {
    step: 4,
    title: "Share anywhere",
    description: "Share your digital identity instantly through QR codes, profile links, or PDF exports.",
  },
] as const;

export const TESTIMONIALS = [
  {
    quote:
      "YourQcard replaced our plastic badges in weeks. HR issues cards remotely and security verifies at the door in seconds.",
    author: "Operations Director",
    company: "Financial services",
  },
  {
    quote:
      "The admin dashboard gives us full visibility over licenses and employees. Onboarding that used to take days now takes minutes.",
    author: "IT Manager",
    company: "Technology partner",
  },
  {
    quote:
      "Branded digital profiles make our team look polished at every client meeting. The QR verification builds instant trust.",
    author: "Head of Business Development",
    company: "Professional services",
  },
] as const;

/** In-browser demo card when no live API demo key is configured. */
export const DEMO_EMPLOYEE: Employee & Record<string, unknown> = {
  id: "demo",
  name: "Sarah Mitchell",
  firstName: "Sarah",
  lastName: "Mitchell",
  title: "Senior Solutions Architect",
  email: "smitchell@yourqcard.com",
  mobileNumber: "70123456",
  countryCode: "961",
  image: `${getBasePathWithoutTrailingSlash()}/media/avatar.jpg`,
  isDemo: true,
  isActive: true,
  companyName: DEMO_COMPANY_NAME,
  companyWebsite: "https://yourqcard.com",
  companyPhone: "1123456",
  companyCountryCode: "961",
  summary:
    "Helping enterprises adopt secure digital identity. Scan my QR code to see YourQcard in action.",
  companyDetails: {
    name: DEMO_COMPANY_NAME,
    logo: getBrandLogoUrl(),
    primaryColor: DEFAULT_DEMO_BRANDING.primaryColor,
    secondaryColor: DEFAULT_DEMO_BRANDING.secondaryColor,
    fontColor: DEFAULT_DEMO_BRANDING.fontColor,
    showLogo: true,
    website: "https://yourqcard.com",
    phoneNumber: "1123456",
    countryCode: "961",
    summary: "Digital workforce identity for modern teams.",
    facebookUrl: "https://facebook.com/yourqcard",
    instagramUrl: "https://instagram.com/yourqcard",
    linkedInUrl: "https://linkedin.com/company/yourqcard",
    xUrl: "https://x.com/yourqcard",
  },
};

/** Public demo page path (includes BASE_PATH when deployed under e.g. /UAT). */
export function getDemoProfilePath(): string {
  const base = getBasePathWithoutTrailingSlash();
  const path = `${base}/demo`.replace(/\/{2,}/g, "/");
  return path.startsWith("/") ? path : `/${path}`;
}

/** Bundled product hero video (includes BASE_PATH when deployed under e.g. /UAT). */
export function getDemoVideoUrl(): string {
  const base = getBasePathWithoutTrailingSlash();
  const path = `${base}/videos/hero.mp4`.replace(/\/{2,}/g, "/");
  return path.startsWith("/") ? path : `/${path}`;
}

export const MARKETING_FAQ_CATEGORIES = [
  "General",
  "Getting started",
  "Features",
  "Pricing",
  "Security",
] as const;

export type MarketingFaqCategory = (typeof MARKETING_FAQ_CATEGORIES)[number];

export const MARKETING_FAQ = [
  {
    category: "General" as const,
    q: "What is YourQcard?",
    a: "YourQcard is a digital workforce identity platform by IDS. Organizations issue branded employee ID cards with QR codes, manage licenses from one dashboard, and verify identity instantly.",
  },
  {
    category: "General" as const,
    q: "Who is YourQcard for?",
    a: "Anyone or any organization that wants professional digital IDs for employees, contractors, or field staff without reprinting plastic cards when roles or staff change.",
  },
  {
    category: "Getting started" as const,
    q: "How long does setup take?",
    a: "Most teams complete company setup, branding, and their first employee cards within minutes. Start a free trial from the pricing page no credit card required.",
  },
  {
    category: "Getting started" as const,
    q: "How do I invite my team?",
    a: "Company admins add users from the backoffice with role-based permissions. Managers and owners can issue cards, manage employees, and view license usage from the dashboard.",
  },
  {
    category: "Features" as const,
    q: "Can I use my own branding?",
    a: "Yes. Upload your logo, set brand colors, and configure company profile settings. Branding is applied consistently across all employee cards.",
  },
  {
    category: "Features" as const,
    q: "How do employees share their card?",
    a: "Each profile includes a unique QR code linking to a public verification page. Employees can share via link, export vCard, or download a PDF card for badges and email signatures.",
  },
  {
    category: "Pricing" as const,
    q: "Is there a free trial?",
    a: "Yes. Start with a free trial to explore the platform. Upgrade to seat-based license packages when you are ready to scale.",
  },
  {
    category: "Pricing" as const,
    q: "Can I change plans later?",
    a: "Absolutely. Upgrade or adjust license packages from your company dashboard as your team grows.",
  },
  {
    category: "Security" as const,
    q: "How secure are QR verifications?",
    a: "QR codes link to server-validated profiles. License status and identity data are checked in real time so expired or invalid cards are clearly surfaced.",
  },
  {
    category: "Security" as const,
    q: "How is my data protected?",
    a: "YourQcard uses multi-tenant isolation per company, reCAPTCHA on public forms, and role-based access controls. See our Privacy Policy for full details.",
  },
] as const;

export const CONTACT_INFO = {
  email: "info@yourqcard.com",
  phone: "+9611859501",
  location: "Beirut, Lebanon",
  hours: "Mon–Fri, 9:00–18:00 (EET)",
  responseTime: "Within one business day",
} as const;
